import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Typography,
  Stack,
  Container,
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy'
import {
  Source as SourceIcon,
  Assessment as ReportIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material'
import { usePermissions } from '../hooks/usePermissions'
import CustomerOriginReports from './CustomerOriginReports'
import CustomerOrigins from './CustomerOrigins'

type TabKey = 'analisis' | 'gestion'

/**
 * Hub unificado de "Origen de Clientes".
 * Combina en una sola página, mediante pestañas:
 *   - Análisis  → reportes/gráficos (CustomerOriginReports)
 *   - Gestión   → tabla CRUD de orígenes (CustomerOrigins)
 *
 * Respeta el RBAC: cada pestaña se muestra solo si el usuario tiene su módulo.
 *   - Análisis  ⇒ módulo 'customer_origins_reports'
 *   - Gestión   ⇒ módulo 'customer_origins'
 * Soporta deep-link con ?tab=analisis | ?tab=gestion (compatibilidad con
 * la ruta antigua /customer-origins/reports).
 */
export default function CustomerOriginsHub() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { canAccess } = usePermissions()

  const canReports = canAccess('customer_origins_reports')
  const canManage = canAccess('customer_origins')

  // Resolver pestaña inicial respetando permisos disponibles.
  const resolveTab = (requested: string | null): TabKey => {
    const wanted: TabKey = requested === 'gestion' ? 'gestion' : 'analisis'
    if (wanted === 'analisis' && !canReports) return 'gestion'
    if (wanted === 'gestion' && !canManage) return 'analisis'
    return wanted
  }

  const [activeTab, setActiveTab] = useState<TabKey>(() =>
    resolveTab(searchParams.get('tab'))
  )

  // Mantener sincronizada la pestaña con el query param (para enlaces compartibles).
  useEffect(() => {
    const current = searchParams.get('tab')
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams)
      next.set('tab', activeTab)
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const handleChange = (value: TabKey) => setActiveTab(value)

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Header unificado */}
        <Stack direction="row" spacing={2} alignItems="center">
          <SourceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography level="h2">Origen de Clientes</Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              Analiza y gestiona de dónde provienen tus clientes
            </Typography>
          </Box>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, value) => handleChange(value as TabKey)}
          sx={{ bgcolor: 'transparent' }}
        >
          <TabList>
            {canReports && (
              <Tab value="analisis" indicatorInset>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReportIcon fontSize="small" />
                  <span>Análisis</span>
                </Stack>
              </Tab>
            )}
            {canManage && (
              <Tab value="gestion" indicatorInset>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SettingsIcon fontSize="small" />
                  <span>Gestión de Orígenes</span>
                </Stack>
              </Tab>
            )}
          </TabList>

          {canReports && (
            <TabPanel value="analisis" sx={{ px: 0 }}>
              <CustomerOriginReports embedded />
            </TabPanel>
          )}
          {canManage && (
            <TabPanel value="gestion" sx={{ px: 0 }}>
              <CustomerOrigins embedded />
            </TabPanel>
          )}
        </Tabs>
      </Stack>
    </Container>
  )
}
